import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Logo from '../Logo';
import { useAuth } from '../../context/AuthContext';
import { useSites } from '../../context/SiteContext';

const navGroups = [
  {
    title: '统计报告',
    items: [
      { to: '/dashboard', label: '综合报告', icon: 'ri-dashboard-3-line', end: true },
      { to: '/dashboard/realtime', label: '实时访客', icon: 'ri-radar-line' },
      { to: '/dashboard/hourly', label: '分时统计', icon: 'ri-time-line' },
      { to: '/dashboard/logs', label: '访问明细', icon: 'ri-list-check-2' },
    ],
  },
  {
    title: '来源分析',
    items: [
      { to: '/dashboard/sources', label: '全部来源', icon: 'ri-links-line' },
      { to: '/dashboard/engines', label: '搜索引擎', icon: 'ri-search-eye-line' },
      { to: '/dashboard/keywords', label: '搜索词', icon: 'ri-keyboard-line' },
      { to: '/dashboard/referrers', label: '外部链接', icon: 'ri-share-forward-line' },
      { to: '/dashboard/social', label: '社交媒体', icon: 'ri-wechat-line' },
    ],
  },
  {
    title: '受访分析',
    items: [
      { to: '/dashboard/pages', label: '受访页面', icon: 'ri-file-chart-line' },
      { to: '/dashboard/hosts', label: '受访域名', icon: 'ri-window-line' },
      { to: '/dashboard/entry', label: '入口页面', icon: 'ri-login-box-line' },
      { to: '/dashboard/exit', label: '出口页面', icon: 'ri-logout-box-line' },
    ],
  },
  {
    title: '访客分析',
    items: [
      { to: '/dashboard/geo', label: '地域分布', icon: 'ri-map-pin-2-line' },
      { to: '/dashboard/visitors', label: '新老访客', icon: 'ri-user-shared-line' },
      { to: '/dashboard/env', label: '系统环境', icon: 'ri-computer-line' },
    ],
  },
  {
    title: '其他',
    items: [
      { to: '/dashboard/spiders', label: '蜘蛛统计', icon: 'ri-bug-line' },
      { to: '/sites', label: '网站列表', icon: 'ri-global-line' },
      { to: '/api-settings', label: 'API设置', icon: 'ri-key-2-line' },
      { to: '/account', label: '我的账号', icon: 'ri-user-settings-line' },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-4 p-3">
      {navGroups.map((group) => (
        <div key={group.title}>
          <div className="px-3 pb-1 text-[11px] font-semibold tracking-wide text-foreground-400">
            {group.title}
          </div>
          <div className="flex flex-col gap-1">
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                <i className={`${item.icon} text-base`} />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function AppShell() {
  const { user, logout } = useAuth();
  const { sites, currentSite, setCurrentSite } = useSites();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background-50">
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 left-0 border-r border-background-200 bg-background-50">
        <div className="h-16 px-5 flex items-center border-b border-background-200">
          <Link to="/sites">
            <Logo />
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Sidebar />
        </div>
        <div className="p-4 border-t border-background-200 text-xs text-foreground-500">
          用心统计每一次访问
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 bg-background-50/90 backdrop-blur-md border-b border-background-200 px-4 md:px-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
            {sites.length > 0 ? (
              <select
                value={currentSite?.id || ''}
                onChange={(e) => setCurrentSite(e.target.value)}
                className="max-w-[220px] truncate px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm text-foreground-500">尚未添加站点</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/sites')}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-background-300 text-foreground-800 rounded-md"
            >
              返回网站列表
            </button>
            <div className="hidden md:block text-right">
              <div className="text-sm font-medium text-foreground-900">{user?.email}</div>
              <div className="text-[11px] text-foreground-500">已登录</div>
            </div>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:text-primary-600"
              aria-label="退出登录"
            >
              <i className="ri-logout-box-r-line" />
            </button>
          </div>
        </header>

        {menuOpen && (
          <div className="lg:hidden border-b border-background-200 bg-background-50 max-h-[70vh] overflow-y-auto">
            <Sidebar onNavigate={() => setMenuOpen(false)} />
          </div>
        )}

        <main className="px-4 md:px-6 py-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AppLayout() {
  return <AppShell />;
}
